import * as Popover from "@radix-ui/react-popover"
import { useTranslation } from "react-i18next"
import { Button } from "./Button"
import { IconName } from "../icons/icons"
import { markOnboardingShown } from "@/store/app-state"
import { Icon } from "../icons/Icon"
import { ReactNode } from "react"
import { PopperContentProps } from "@radix-ui/react-popper"
import { analytics } from "@/telemetry/amplitude"
import { useAtomValue } from "jotai"
import { dialogsAtom } from "@/store/dialogs-state"
import { OnboardingState } from "@/store/onboarding/onboarding-state"
import { isElectronApp } from "@/utils/electron"

export interface TooltipProps {
	title?: string
	main: string
	action?: string
	actionIcon?: IconName
	actionHandler?: () => void
	index?: number
	totalCount?: number
	onboardingKey?: keyof OnboardingState
}

export function Tooltip(props: TooltipProps) {
	const { t } = useTranslation("components")
	return (
		<div
			aria-label={t("floatingTooltip.ariaTooltip")}
			className="flex w-full flex-col space-y-2 p-3"
		>
			{props.title && (
				<div className="text-start text-md font-bold text-black">{props.title}</div>
			)}

			<p className="whitespace-pre-line text-sm text-base-300 dark:text-base-500">
				<span>{props.main}</span>
				{props.totalCount && props.action && (
					<span
						className="flex items-center space-x-1 py-2 text-accent-600 transition-colors hover:text-accent-500"
						onClick={(e) => {
							analytics.trackEvent(
								{
									label: "open_action",
									type: "click",
									value: props.action,
								},
								e.target,
							)
							props.actionHandler?.()
						}}
					>
						{props.actionIcon && (
							<span className="inline-block pt-0.5">
								<Icon name={props.actionIcon} size={14} />
							</span>
						)}
						<span>{props.action}</span>
					</span>
				)}
			</p>
			{props.totalCount && props.index && (
				<div className="flex flex-row items-center justify-between pt-2">
					<div className="text-xs text-base-400">
						{props.totalCount > 1 ? `${props.index}/${props.totalCount}` : ""}
					</div>

					<div className="flex flex-row items-center justify-between space-x-2">
						{props.index > 1 && (
							<Button
								trackingLabel="previous"
								variant="custom"
								className="border-0 bg-transparent font-semibold text-base-500 hover:text-base-200 dark:text-base-600 dark:hover:text-base-800"
								onClick={() => {
									if (props.onboardingKey && props.index) {
										// Go back to previous step
										markOnboardingShown(props.onboardingKey, props.index - 2)
									}
								}}
							>
								{t("floatingTooltip.previous")}
							</Button>
						)}

						<Button
							trackingLabel={props.index === props.totalCount ? "done" : "next"}
							variant="primary"
							onClick={() => {
								if (props.onboardingKey && props.index) {
									markOnboardingShown(props.onboardingKey, props.index)
								}
							}}
						>
							{props.index === props.totalCount
								? t("floatingTooltip.gotIt")
								: t("floatingTooltip.next")}
						</Button>
					</div>
				</div>
			)}

			{!props.totalCount && props.action && props.actionHandler && (
				<div className="flex flex-row items-center justify-end pt-2">
					<Button
						trackingLabel={props.action}
						variant="primary"
						onClick={() => {
							props.actionHandler?.()
						}}
					>
						{props.action}
					</Button>
				</div>
			)}
		</div>
	)
}

type FloatingTooltipProps = PopperContentProps & {
	open: boolean
	children: ReactNode
	tooltip?: TooltipProps
}

export const FloatingTooltip = ({ children, open, tooltip, ...props }: FloatingTooltipProps) => {
	const isDialogShowing = useAtomValue(dialogsAtom).length > 0
	const collisionPadding = isElectronApp ? { top: 38, right: 8, bottom: 8, left: 8 } : 8

	return (
		<Popover.Root open={!isDialogShowing && open}>
			<Popover.Anchor asChild>{children}</Popover.Anchor>
			<Popover.Portal>
				<Popover.Content
					className="relative z-20 w-[300px] rounded-md bg-white shadow-xl data-[state=closed]:animate-floating-tooltip-out data-[state=open]:animate-floating-tooltip-in"
					collisionPadding={collisionPadding}
					{...props}
				>
					{tooltip && <Tooltip {...tooltip} />}
					<Popover.Arrow className="scale-110 fill-white" />
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	)
}
